import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Drawer,
    IconButton,
    Typography,
    Box,
    Button,
    CircularProgress,
    TextField,
    Grid,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Tooltip,
    InputAdornment,
} from '@mui/material';
import { Close, Add, Delete, Build, LinkOff, Settings, Search } from '@mui/icons-material';
import { DynamicDataGrid } from '../common/DynamicContainer';
import { toast } from 'react-toastify';

const DRAWER_WIDTH = 720;
const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}asrosync/tools`;

const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(localStorage.getItem('token') && { Authorization: `Bearer ${localStorage.getItem('token')}` }),
});

const fetchToolsForAgent = async (agentId, mode) => {
    const response = await fetch(
        `${API_BASE_URL}/available?tenantAgentId=${encodeURIComponent(agentId)}&mode=${mode}`,
        { method: 'GET', headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch tools');
    const data = await response.json();
    return Array.isArray(data) ? data : [];
};

const fetchConnectorsByMcId = async (tenantId, mcId) => {
    const response = await fetch(
        `${API_BASE_URL}/connectors/tenant/${tenantId}?mcId=${encodeURIComponent(mcId)}`,
        { headers: getAuthHeaders() }
    );
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? data : [];
};

const AgentToolsDrawer = ({ open, onClose, agent, tenantId }) => {
    const effectiveTenantId = agent?.tenantId ?? agent?.tenant_id ?? tenantId;
    const agentId = agent?.id || agent?.tenantAgentId;

    const [loading, setLoading] = useState(false);
    const [assignedTools, setAssignedTools] = useState([]);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addableLoading, setAddableLoading] = useState(false);
    const [addableOptions, setAddableOptions] = useState([]);
    /** Multi-select: global tool ids to assign in one Add click */
    const [pickGlobalToolIds, setPickGlobalToolIds] = useState([]);
    const [toolSearchQuery, setToolSearchQuery] = useState('');
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

    const [connectorsByMcId, setConnectorsByMcId] = useState({});
    const connectorsByMcIdRef = useRef({});
    const [loadingMcIds, setLoadingMcIds] = useState(() => new Set());
    const [connectingToolIds, setConnectingToolIds] = useState(() => new Set());
    const [saving, setSaving] = useState(false);
    const [removing, setRemoving] = useState(false);

    const [credentialsDialogTool, setCredentialsDialogTool] = useState(null);
    const [credentialsFormValues, setCredentialsFormValues] = useState({});

    /** @type {{ action: 'disconnect' | 'remove', tool: object } | null} */
    const [confirmDialog, setConfirmDialog] = useState(null);

    const loadAssigned = useCallback(async (opts = {}) => {
        if (!agentId) return;
        const silent = opts.silent === true;
        // Full loading unmounts the grid and would reset pageSize via initialState.
        if (!silent) setLoading(true);
        try {
            const data = await fetchToolsForAgent(agentId, 'assigned');
            setAssignedTools(data);
        } catch (error) {
            console.error(error);
            toast.error('Failed to load agent tools');
            setAssignedTools([]);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [agentId]);

    const ensureConnectorsLoaded = useCallback(
        async (mcId) => {
            if (!mcId || !effectiveTenantId) return;
            const key = String(mcId);
            if (connectorsByMcId[key] !== undefined) return;

            setLoadingMcIds((prev) => new Set([...prev, key]));
            try {
                const list = await fetchConnectorsByMcId(effectiveTenantId, mcId);
                setConnectorsByMcId((prev) => ({ ...prev, [key]: list }));
            } catch {
                setConnectorsByMcId((prev) => ({ ...prev, [key]: [] }));
            } finally {
                setLoadingMcIds((prev) => {
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                });
            }
        },
        [effectiveTenantId, connectorsByMcId]
    );

    useEffect(() => {
        if (open && agent) {
            setAddDialogOpen(false);
            setPickGlobalToolIds([]);
            setToolSearchQuery('');
            setPaginationModel({ page: 0, pageSize: 25 });
            setConnectorsByMcId({});
            connectorsByMcIdRef.current = {};
            setCredentialsDialogTool(null);
            setCredentialsFormValues({});
            loadAssigned();
        }
    }, [open, agent, loadAssigned]);

    useEffect(() => {
        if (!effectiveTenantId || assignedTools.length === 0) return;
        const mcIds = [
            ...new Set(assignedTools.map((t) => t.toolMcId).filter(Boolean)),
        ];
        mcIds.forEach((mcId) => ensureConnectorsLoaded(mcId));
    }, [assignedTools, effectiveTenantId, ensureConnectorsLoaded]);

    const openAddDialog = async () => {
        setAddDialogOpen(true);
        setPickGlobalToolIds([]);
        setAddableLoading(true);
        try {
            const data = await fetchToolsForAgent(agentId, 'addable');
            setAddableOptions(data);
        } catch (e) {
            toast.error('Failed to load tenant tools');
            setAddableOptions([]);
        } finally {
            setAddableLoading(false);
        }
    };

    const handleAddDialogConfirm = async () => {
        const selectedIds = [...new Set((pickGlobalToolIds || []).map(String).filter(Boolean))];
        if (selectedIds.length === 0) {
            toast.error('Select at least one tool');
            return;
        }
        const alreadyOnAgent = new Set(
            (assignedTools || []).map((t) => String(t.toolId)).filter(Boolean)
        );
        const toAssign = selectedIds
            .filter((id) => !alreadyOnAgent.has(id))
            .map((id) => addableOptions.find((t) => String(t.toolId) === id))
            .filter(Boolean);

        if (toAssign.length === 0) {
            toast.info('Selected tools are already on this agent');
            setAddDialogOpen(false);
            setPickGlobalToolIds([]);
            return;
        }

        setSaving(true);
        const okNames = [];
        const failMessages = [];
        try {
            for (const tool of toAssign) {
                try {
                    const response = await fetch(`${API_BASE_URL}/assign`, {
                        method: 'POST',
                        headers: getAuthHeaders(),
                        body: JSON.stringify({
                            tenantAgentId: agentId,
                            globalToolId: tool.toolId,
                        }),
                    });
                    const result = await response.json().catch(() => ({}));
                    if (!response.ok) {
                        const msg = result.message || result.error || 'Failed to assign';
                        // Treat duplicate / already-assigned as skip, not hard fail
                        if (/already|duplicate|exists/i.test(String(msg))) {
                            continue;
                        }
                        failMessages.push(`${tool.toolName}: ${msg}`);
                        continue;
                    }
                    okNames.push(tool.toolName || String(tool.toolId));
                } catch (e) {
                    failMessages.push(`${tool.toolName || tool.toolId}: ${e.message || 'Failed'}`);
                }
            }

            if (okNames.length > 0) {
                toast.success(
                    okNames.length === 1
                        ? `${okNames[0]} assigned to agent`
                        : `${okNames.length} tools assigned to agent`
                );
            }
            if (failMessages.length > 0) {
                toast.error(
                    failMessages.length === 1
                        ? failMessages[0]
                        : `${failMessages.length} tool(s) failed to assign`
                );
            }
            setAddDialogOpen(false);
            setPickGlobalToolIds([]);
            await loadAssigned({ silent: true });
        } finally {
            setSaving(false);
        }
    };

    const runRemoveTool = async (tool) => {
        setRemoving(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/unassign?tenantAgentId=${encodeURIComponent(agentId)}&globalToolId=${encodeURIComponent(tool.toolId)}`,
                { method: 'DELETE', headers: getAuthHeaders() }
            );
            if (!response.ok && response.status !== 404) {
                throw new Error(await response.text());
            }
            toast.success('Tool removed from agent');
            await loadAssigned({ silent: true });
        } catch (e) {
            toast.error(e.message || 'Failed to remove tool');
        } finally {
            setRemoving(false);
        }
    };

    const requestDisconnect = (tool) => {
        if (!tool?.toolId) return;
        setConfirmDialog({ action: 'disconnect', tool });
    };

    const runDisconnectTool = async (tool) => {
        setSaving(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/disconnect?tenantAgentId=${encodeURIComponent(agentId)}&globalToolId=${encodeURIComponent(tool.toolId)}`,
                { method: 'DELETE', headers: getAuthHeaders() }
            );
            const result = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(result.message || result.error || 'Disconnect failed');
            }
            toast.success('Connector disconnected');
            await loadAssigned({ silent: true });
        } catch (e) {
            toast.error(e.message || 'Disconnect failed');
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmDialog = async () => {
        if (!confirmDialog) return;
        const { action, tool } = confirmDialog;
        setConfirmDialog(null);
        if (action === 'disconnect') {
            await runDisconnectTool(tool);
        } else if (action === 'remove') {
            await runRemoveTool(tool);
        }
    };

    const handleOAuthPopup = (result, onDone) => {
        if (!result.authUrl) return false;
        const popup = window.open(result.authUrl, 'oauth_window', 'width=600,height=700,scrollbars=yes');
        if (!popup) {
            toast.error('Allow popups and try again');
            return true;
        }
        const iv = setInterval(() => {
            if (popup.closed) {
                clearInterval(iv);
                toast.success('Tool connected');
                onDone();
            }
        }, 500);
        return true;
    };

    const connectToolToConnector = async (tool, connectorId) => {
        if (!tool || !connectorId) return;
        const rowId = String(tool.tenantToolId ?? tool.toolId);
        setConnectingToolIds((prev) => new Set([...prev, rowId]));
        try {
            const response = await fetch(`${API_BASE_URL}/configure`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    tenantAgentId: agentId,
                    globalToolId: tool.toolId,
                    authCredentials: '{}',
                    syncConnectorId: connectorId,
                }),
            });
            const result = await response.json().catch(() => ({}));
            if (handleOAuthPopup(result, () => loadAssigned({ silent: true }))) return;
            if (!response.ok) throw new Error(result.message || result.error || 'Connect failed');
            toast.success(`${tool.toolName} connected`);
            await loadAssigned({ silent: true });
        } catch (e) {
            toast.error(e.message || 'Connect failed');
        } finally {
            setConnectingToolIds((prev) => {
                const next = new Set(prev);
                next.delete(rowId);
                return next;
            });
        }
    };

    const handleConnectorDropdownChange = (tool, connectorId) => {
        if (!connectorId) return;
        if (tool.syncConnectorId && String(tool.syncConnectorId) === String(connectorId)) return;
        connectToolToConnector(tool, connectorId);
    };

    const openCredentialsDialog = (tool) => {
        setCredentialsDialogTool(tool);
        setCredentialsFormValues({});
    };

    const closeCredentialsDialog = () => {
        setCredentialsDialogTool(null);
        setCredentialsFormValues({});
    };

    const handleSaveCredentials = async () => {
        if (!credentialsDialogTool) return;
        setSaving(true);
        try {
            const authCredentials = credentialsFormValues.raw != null
                ? credentialsFormValues.raw
                : JSON.stringify(credentialsFormValues);
            const response = await fetch(`${API_BASE_URL}/configure`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    tenantAgentId: agentId,
                    globalToolId: credentialsDialogTool.toolId,
                    authCredentials,
                }),
            });
            const result = await response.json().catch(() => ({}));
            if (handleOAuthPopup(result, () => {
                closeCredentialsDialog();
                loadAssigned({ silent: true });
            })) return;
            if (!response.ok) throw new Error(result.message || result.error || 'Configure failed');
            toast.success('Tool configured');
            closeCredentialsDialog();
            await loadAssigned({ silent: true });
        } catch (e) {
            toast.error(e.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const gridRows = useMemo(
        () =>
            assignedTools.map((t) => {
                const hasConnector = !!(t.syncConnectorId || t.syncConnectorName);
                const connected = !!t.isConnected || hasConnector;
                return {
                    id: t.tenantToolId ?? t.toolId,
                    toolName: t.toolName,
                    toolType: t.toolType || '—',
                    isConnected: connected,
                    usesConnector: !!t.toolMcId,
                    _tool: t,
                };
            }),
        [assignedTools]
    );

    const filteredGridRows = useMemo(() => {
        const q = toolSearchQuery.trim().toLowerCase();
        if (!q) return gridRows;
        return gridRows.filter((row) => {
            const hay = [row.toolName, row.toolType, row._tool?.toolUniqueId]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [gridRows, toolSearchQuery]);

    const renderConnectorDropdown = (tool) => {
        const rowId = String(tool.tenantToolId ?? tool.toolId);
        const mcKey = String(tool.toolMcId);
        const isConnecting = connectingToolIds.has(rowId);
        const isLoadingList = loadingMcIds.has(mcKey);
        const options = connectorsByMcId[mcKey];

        if (isConnecting || isLoadingList) {
            return <CircularProgress size={20} />;
        }

        if (options === undefined) {
            return <CircularProgress size={20} />;
        }

        if (options.length === 0) {
            return (
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                    No connectors — add one in Connector Token
                </Typography>
            );
        }

        const value = tool.syncConnectorId ? String(tool.syncConnectorId) : '';

        return (
            <Select
                size="small"
                displayEmpty
                fullWidth
                value={value}
                disabled={saving || removing || isConnecting}
                onChange={(e) => handleConnectorDropdownChange(tool, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                sx={{
                    minWidth: 0,
                    height: 28,
                    fontSize: '0.8125rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '6px',
                    },
                    '& .MuiSelect-select.MuiOutlinedInput-input': {
                        py: '2px',
                        pl: 1,
                        pr: '24px !important',
                        minHeight: '24px !important',
                        lineHeight: 1.3,
                        display: 'flex',
                        alignItems: 'center',
                    },
                    '& .MuiSelect-icon': {
                        fontSize: '1.125rem',
                        right: 2,
                        top: 'calc(50% - 9px)',
                    },
                }}
                renderValue={(selected) => {
                    if (!selected) return <em>Select connector</em>;
                    const match = options.find((c) => String(c.id) === String(selected));
                    return match?.name || match?.description || tool.syncConnectorName || selected;
                }}
            >
                <MenuItem value="" disabled>
                    <em>Select connector</em>
                </MenuItem>
                {options.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                        {c.name || c.description || c.id}
                    </MenuItem>
                ))}
            </Select>
        );
    };

    const renderCredentialForm = () => {
        if (!credentialsDialogTool?.authConfig) {
            return <Typography color="text.secondary">No credentials required.</Typography>;
        }
        try {
            const fields = JSON.parse(credentialsDialogTool.authConfig);
            if (!Array.isArray(fields)) {
                return (
                    <TextField
                        fullWidth
                        multiline
                        rows={8}
                        label="Credentials JSON"
                        value={credentialsFormValues.raw || ''}
                        onChange={(e) => setCredentialsFormValues({ raw: e.target.value })}
                    />
                );
            }
            return (
                <Grid container spacing={2}>
                    {fields
                        .filter((f) => !f.hidden)
                        .map((field) => (
                            <Grid item xs={12} key={field.key}>
                                <TextField
                                    fullWidth
                                    label={field.label}
                                    type={field.type === 'password' ? 'password' : 'text'}
                                    required={field.required}
                                    value={credentialsFormValues[field.key] || ''}
                                    onChange={(e) =>
                                        setCredentialsFormValues((p) => ({
                                            ...p,
                                            [field.key]: e.target.value,
                                        }))
                                    }
                                />
                            </Grid>
                        ))}
                </Grid>
            );
        } catch {
            return <Alert severity="error">Invalid auth config</Alert>;
        }
    };

    const columns = [
        { field: 'toolName', headerName: 'Tool', flex: 1, minWidth: 160 },
        { field: 'toolType', headerName: 'Type', width: 110 },
        {
            field: 'connector',
            headerName: 'Sync connector',
            flex: 1.2,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => {
                const tool = params.row._tool;
                if (params.row.usesConnector) {
                    return renderConnectorDropdown(tool);
                }
                return (
                    <Typography variant="body2" color="text.secondary">
                        Manual credentials
                    </Typography>
                );
            },
        },
        {
            field: 'actions',
            headerName: '',
            width: 120,
            sortable: false,
            renderCell: (params) => {
                const tool = params.row._tool;
                const busy = saving || removing || connectingToolIds.has(String(params.row.id));
                return (
                    <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                        {!params.row.usesConnector && (
                            <Tooltip title="Configure credentials">
                                <span>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        disabled={busy}
                                        onClick={() => openCredentialsDialog(tool)}
                                    >
                                        <Settings fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        {params.row.isConnected && (
                            <Tooltip title="Disconnect connector">
                                <span>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        disabled={busy}
                                        onClick={() => requestDisconnect(tool)}
                                    >
                                        <LinkOff fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title="Remove tool from agent">
                            <span>
                                <IconButton
                                    size="small"
                                    color="error"
                                    disabled={busy}
                                    onClick={() => setConfirmDialog({ action: 'remove', tool })}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: DRAWER_WIDTH } }}>
            <Box
                sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <Build fontSize="small" color="primary" />
                <Typography variant="h6" sx={{ flex: 1 }}>
                    Tools — {agent?.agentName}
                </Typography>
                <IconButton onClick={onClose} aria-label="Close">
                    <Close />
                </IconButton>
            </Box>

            <Box sx={{ p: 2, flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                <Alert severity="info" sx={{ mb: 1, py: 0.5, '& .MuiAlert-message': { py: 0.25 } }}>
                    Pick a <strong>Sync connector</strong> from the dropdown (same list as Connector Token, filtered
                    by tool type). <strong>Add</strong> can assign multiple tenant tools at once;{' '}
                    <strong>Remove</strong> drops a tool from this agent.
                </Alert>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                    }}
                >
                    <TextField
                        size="small"
                        placeholder="Search tools..."
                        value={toolSearchQuery}
                        onChange={(e) => setToolSearchQuery(e.target.value)}
                        disabled={loading || assignedTools.length === 0}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                    <Search sx={{ fontSize: 16, color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: 200,
                            '& .MuiOutlinedInput-root': {
                                height: 32,
                                fontSize: '0.8125rem',
                                bgcolor: 'background.paper',
                            },
                            '& .MuiOutlinedInput-input': {
                                py: 0.5,
                                px: 0.5,
                            },
                        }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Add one or more tools from tenant catalog">
                        <span>
                            <IconButton color="primary" size="small" onClick={openAddDialog} disabled={loading}>
                                <Add fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box sx={{ flex: 1, minHeight: 260 }}>
                            <DynamicDataGrid
                                rows={filteredGridRows}
                                columns={columns}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                            />
                        </Box>
                        {assignedTools.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                No tools on this agent. Click <strong>Add</strong>.
                            </Typography>
                        ) : filteredGridRows.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                No tools match &quot;{toolSearchQuery.trim()}&quot;. Clear search to see all tools.
                            </Typography>
                        ) : null}
                    </>
                )}
            </Box>

            <Dialog
                open={!!confirmDialog}
                onClose={() => setConfirmDialog(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {confirmDialog?.action === 'disconnect' ? 'Disconnect connector' : 'Remove tool'}
                </DialogTitle>
                <DialogContent>
                    {confirmDialog?.action === 'disconnect' ? (
                        <Alert severity="info" sx={{ mt: 0.5 }}>
                            Disconnect <strong>{confirmDialog.tool.toolName}</strong>
                            {confirmDialog.tool.syncConnectorName ? (
                                <>
                                    {' '}
                                    from sync connector <strong>{confirmDialog.tool.syncConnectorName}</strong>
                                </>
                            ) : null}
                            ? The tool stays on this agent; pick another connector from the dropdown later.
                        </Alert>
                    ) : (
                        <Alert severity="warning" sx={{ mt: 0.5 }}>
                            Remove <strong>{confirmDialog?.tool?.toolName}</strong> from this agent? This cannot be
                            undone.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog(null)} disabled={saving || removing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDialog}
                        color={confirmDialog?.action === 'remove' ? 'error' : 'primary'}
                        variant="contained"
                        disabled={saving || removing}
                    >
                        {confirmDialog?.action === 'disconnect' ? 'Disconnect' : 'Remove'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={addDialogOpen} onClose={() => !saving && setAddDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add tools to agent</DialogTitle>
                <DialogContent>
                    {addableLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : addableOptions.length === 0 ? (
                        <Typography color="text.secondary" sx={{ pt: 1 }}>
                            No tenant tools available to add. Provision tools under AI Hub → Tenant Tools, or all are
                            already on this agent.
                        </Typography>
                    ) : (
                        <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel id="add-agent-tools-label">Tenant tools</InputLabel>
                            <Select
                                labelId="add-agent-tools-label"
                                multiple
                                value={pickGlobalToolIds}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setPickGlobalToolIds(
                                        typeof v === 'string' ? v.split(',') : v.map(String)
                                    );
                                }}
                                input={<OutlinedInput label="Tenant tools" />}
                                renderValue={(selected) => {
                                    const labels = (selected || []).map((id) => {
                                        const t = addableOptions.find((o) => String(o.toolId) === String(id));
                                        return t
                                            ? `${t.toolName}${t.toolType ? ` (${t.toolType})` : ''}`
                                            : String(id);
                                    });
                                    return labels.join(', ');
                                }}
                                MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
                            >
                                {addableOptions.map((t) => {
                                    const id = String(t.toolId);
                                    return (
                                        <MenuItem
                                            key={id}
                                            value={id}
                                            dense
                                            sx={{ py: 0.25, minHeight: 28 }}
                                        >
                                            <Checkbox
                                                checked={pickGlobalToolIds.includes(id)}
                                                size="small"
                                                sx={{ py: 0, pl: 0.5, pr: 0.75 }}
                                            />
                                            <ListItemText
                                                primary={
                                                    t.toolType
                                                        ? `${t.toolName} (${t.toolType})`
                                                        : t.toolName
                                                }
                                                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                                sx={{ my: 0 }}
                                            />
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Select one or more tools, then Add. Already-assigned tools are not listed.
                            </Typography>
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddDialogConfirm}
                        disabled={
                            addableLoading ||
                            saving ||
                            pickGlobalToolIds.length === 0 ||
                            addableOptions.length === 0
                        }
                    >
                        {saving
                            ? 'Adding…'
                            : pickGlobalToolIds.length > 1
                              ? `Add ${pickGlobalToolIds.length}`
                              : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!credentialsDialogTool}
                onClose={closeCredentialsDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Configure — {credentialsDialogTool?.toolName}</DialogTitle>
                <DialogContent>{renderCredentialForm()}</DialogContent>
                <DialogActions>
                    <Button onClick={closeCredentialsDialog} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSaveCredentials} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Drawer>
    );
};

export default AgentToolsDrawer;
